<template>
    <div v-if="orders.length > 0" class="row">
        <div class="col-md-12">
            <div class="card">
                <div class="card-header text-black p-2 d-flex justify-content-between align-items-center">
                    {{ title }}
                    <button type="button" class="btn btn-sm btn-outline-danger" @click="closeModal">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x-icon lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                </div>
                <div class="card-body overflow-auto p-2">
                    <div class="table-responsive">
                        <table class="table hm-fs-12 align-middle table-hover table-sm">
                            <thead>
                                <tr>
                                    <th class="bg-theme">Assigned To</th>
                                    <th class="bg-theme">Client</th>
                                    <th class="bg-theme">Address</th>
                                    <th class="bg-theme">Postal Code</th>
                                    <th class="bg-theme">Phone</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="orders.length == 0">
                                    <td colspan="5" class="text-center">No orders found</td>
                                </tr>
                                <tr 
                                    v-else 
                                    v-for="order in orders" 
                                    :key="order.id"
                                    :id="'order-' + order.id"
                                    @contextmenu.prevent.stop="showContextMenu($event, order)"
                                >
                                    <td>
                                        {{ 
                                            order?.handymans?.map(item => {
                                                // Check if handyman has provider role
                                                const hasProviderRole = item?.roles?.some(role => role.name === 'provider');
                                                return hasProviderRole ? 'Admin' : item?.name;
                                            }).join(', ')
                                        }}
                                    </td>
                                    <td>{{ order.customer.name }}</td>
                                    <td>{{ order.address.address + ', ' + order.address.address_1 }}</td>
                                    <td>{{ order.address.postal_code }}</td>
                                    <td>{{ order.customer.phone_number }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- Right click context -->
    <vue-simple-context-menu
        element-id="handleRightClickSummaryOrder"
        :options="contextMenuOptions"
        ref="rightClickMenu"
        @option-clicked="handleRightClickMenuAction"
        :fixed="true"
    >
    </vue-simple-context-menu>

    <!-- Customer Details Modal -->
    <CustomerDetails
        v-if="selectedOrder"
        ref="customerDetails" 
        :order="selectedOrder" 
    />

    <!-- Add Attachment Modal -->
    <AddAttachment
        v-if="selectedOrder"
        ref="addAttachmentModal"
        :orderId="selectedOrder.id"
    />

    <!-- Move order to pending -->
    <MoveToPending
        v-if="selectedOrder"
        ref="moveToPendingModal"
        :order="selectedOrder"
    />

    <!-- Move order to processing -->
    <MoveToProcessing
        v-if="selectedOrder"
        ref="moveToProcessingModal"
        :order="selectedOrder"
    />

    <!-- Cancel Order Modal -->
    <CancelOrder
        v-if="selectedOrder"
        ref="cancelOrderModal"
        :order="selectedOrder"
    />

    <!-- Assign Order Modal -->
    <AssignOrder
        v-if="selectedOrder"
        ref="assignOrderModal"
        :order="selectedOrder"
        :action="assignOrReassign"
    />

    <!-- Quotation Modal -->
    <Quotation
        v-if="selectedOrder"
        ref="quotationModal"
        :order="selectedOrder"
        :action="action"
    />

    <!-- Update Order Status Modal -->
    <UpdateOrderStatus
        v-if="selectedOrder"
        ref="updateOrderStatusModal"
        :order="selectedOrder"
        :action="action"
    />

    <!-- Move order to completed -->
    <MoveToCompleted
        v-if="selectedOrder"
        ref="moveToCompletedModal"
        :order="selectedOrder"
    />
</template>
<script>
import VueSimpleContextMenu from "vue-simple-context-menu";
import "vue-simple-context-menu/dist/vue-simple-context-menu.css";

// Load components
import CustomerDetails from "../Order/CustomerDetails.vue";
import AddAttachment from "../Order/AddAttachment.vue";
import CancelOrder from "../Order/CancelOrder.vue";
import AssignOrder from "../Order/AssignOrder.vue";
import MoveToProcessing from "../Order/MoveToProcessing.vue";
import MoveToPending from "../Order/MoveToPending.vue";
import Quotation from "../Order/Quotation.vue";
import UpdateOrderStatus from "../Order/UpdateOrderStatus.vue";
import MoveToCompleted from "../Order/MoveToCompleted.vue";

export default {
    name: "OrderSummary",
    components: {
        VueSimpleContextMenu,
        CustomerDetails,
        AddAttachment,
        CancelOrder,
        AssignOrder,
        MoveToProcessing,
        MoveToPending,
        Quotation,
        UpdateOrderStatus,
        MoveToCompleted,
    },
    emits: ['close-order-summary'],
    props: {
        title: {
            type: String,
            required: true,
            default: 'Order Summary'
        },
        orders: {
            type: Array,
            required: true,
            default: () => []
        }
    },
    data() {
        return {
            action: '',
            assignOrReassign: 'assign',
            selectedOrder: null,
            contextMenuOptions: [],
            availableOptions: {
                pending: [
                    {name: "Move To Assigned", action: "move_to_assigned"},
                    {name: "View Order", action: "order_view" },
                    {name: "View Customer", action: "view_customer" },
                    {name: "Add Attachment", action: "add_attachment" },
                    {name: "Cancel Order", action: "cancel" },
                    //{name: "Duplicate Order", action: "duplicate" },
                ],
                assigned: [
                    {name: "Back To Pending", action: "move_to_pending"},
                    {name: "Move To Processing", action: "move_to_processing"},
                    {name: "View Order", action: "order_view" },
                    {name: "View Customer", action: "view_customer" },
                    {name: "Add Attachment", action: "add_attachment" },
                    {name: "Reassign Order", action: "reassign_order" },
                    {name: "Cancel Order", action: "cancel" },
                    //{name: "Duplicate Order", action: "duplicate" },
                ],
                processing: [
                    {name: "Back To Pending", action: "move_to_pending"},
                    {name: "Back To Assigned", action: "move_to_assigned"},
                    {name: "Move To Quotation", action: "move_to_quotation"},
                    {name: "Move To Completed", action: "move_to_completed"},
                    {name: "View Order", action: "order_view" },
                    {name: "View Customer", action: "view_customer" },
                    {name: "Add Attachment", action: "add_attachment" },
                    {name: "Cancel Order", action: "cancel" },
                    //{name: "Duplicate Order", action: "duplicate" },
                ],
                quotation: [
                    {name: "Back To Processing", action: "move_to_processing"},
                    {name: "Move To Completed", action: "move_to_completed"},
                    {name: "Quotation Declined", action: "quotation_declined"},
                    {name: "Quotation Approved", action: "quotation_approved"},
                    {name: "View Order", action: "order_view" },
                    {name: "View Customer", action: "view_customer" },
                    {name: "Add Attachment", action: "add_attachment" },
                    {name: "Cancel Order", action: "cancel" },
                    //{name: "Duplicate Order", action: "duplicate" },
                ],
                engineer_completed: [
                    {name: "Move To Completed", action: "move_to_completed"},
                    {name: "Move To Quotation", action: "move_to_quotation"},
                    {name: "Back To Processing", action: "move_to_processing"},
                    {name: "View Order", action: "order_view" },
                    {name: "View Customer", action: "view_customer" },
                    {name: "Add Attachment", action: "add_attachment" },
                    {name: "Cancel Order", action: "cancel" },
                    //{name: "Duplicate Order", action: "duplicate" },
                ]
            }
        }
    },
    mounted() {
        this.$emitter.on('reset-selected-order', () => {
            this.selectedOrder = null;
        });
    },
    methods: {
        closeModal() {
            this.selectedOrder = null;
            this.$emit('close-order-summary');
        },
        showContextMenu(event, order) {
            //this.selectedOrder = order;
            this.contextMenuOptions = this.availableOptions[order.status];
            this.$refs.rightClickMenu.showMenu(event, order)
        },
        handleRightClickMenuAction(event) {
            let order = event.item;
            let action = event.option.action;
            this.selectedOrder = order;

            // Check if action is valid
            if (!action) {
                return;
            }

            // If action is view_order then view order
            if (action === 'order_view') {
                window.location.href = "/admin/orders/" + order.order_number;
                return;
            }
            else if(action === 'view_customer') {
                this.$nextTick(() => {
                    if (this.$refs.customerDetails) {
                        this.$refs.customerDetails.showModal();
                    }
                });
                return;
            }
            else if(action === 'add_attachment') {
                this.$nextTick(() => {
                    if (this.$refs.addAttachmentModal) {
                        this.$refs.addAttachmentModal.showModal();
                    }
                });
                return;
            }
            else if(action === 'move_to_assigned') {
                this.assignOrReassign = 'assign';
                this.$nextTick(() => {
                    if (this.$refs.assignOrderModal) {
                        this.$refs.assignOrderModal.showModal();
                    }
                });
                return;
            }
            else if(action === 'reassign_order') {
                this.assignOrReassign = 'reassign';
                this.$nextTick(() => {
                    if (this.$refs.assignOrderModal) {
                        this.$refs.assignOrderModal.showModal();
                    }
                });
                return;
            }
            else if(action === 'move_to_pending') {
                this.$nextTick(() => {
                    if (this.$refs.moveToPendingModal) {
                        this.$refs.moveToPendingModal.showModal();
                    }
                });
                return;
            }
            else if(action === 'move_to_processing') {
                this.$nextTick(() => {
                    if (this.$refs.moveToProcessingModal) {
                        this.$refs.moveToProcessingModal.showModal();
                    }
                });
                return;
            }
            else if(action === 'move_to_quotation') {
                this.$nextTick(() => {
                    if (this.$refs.quotationModal) {
                        this.action = 'move_to_quotation';
                        this.$refs.quotationModal.showModal();
                    }
                });
                return;
            }
            else if(action === 'quotation_declined') {
                this.$nextTick(() => {
                    if (this.$refs.quotationModal) {
                        this.action = 'quotation_declined';
                        this.$refs.quotationModal.showModal();
                    }
                });
                return;
            }
            else if(action === 'quotation_approved') {
                this.$nextTick(() => {
                    if (this.$refs.updateOrderStatusModal) {
                        this.action = 'quotation_approved';
                        this.$refs.updateOrderStatusModal.showModal();
                    }
                });
                return;
            }
            else if(action === 'move_to_completed') {
                this.$nextTick(() => {
                    if (this.$refs.moveToCompletedModal) {
                        this.$refs.moveToCompletedModal.showModal();
                    }
                });
                return;
            }
            else if(action === 'duplicate') {
                this.$nextTick(() => {
                    if (this.$refs.duplicateOrderModal) {
                        this.$refs.duplicateOrderModal.showModal();
                    }
                });
                return;
            }
            else if(action === 'cancel') {
                this.$nextTick(() => {
                    if (this.$refs.cancelOrderModal) {
                        this.$refs.cancelOrderModal.showModal();
                    }
                });
                return;
            }
        },
    }
}
</script>